import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  useTheme,
  Stack,
  Divider,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import levelService from '../../services/level';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const getRewardImageSrc = (item, amount = 0, level = 0) => {
  const name = (item || '').toLowerCase();
  
  if (name.includes('mobile') || name.includes('phone')) {
    return require('../../images/reward_mobile_3d.png');
  }
  if (name.includes('laptop') || name.includes('mac') || name.includes('computer')) {
    return require('../../images/reward_laptop_3d.png');
  }
  if (name.includes('bike') || name.includes('scooter') || name.includes('motorcycle')) {
    if (amount >= 150000) {
      return require('../../images/reward_bike_3d.png');
    }
    return require('../../images/reward_cheap_bike_3d.png');
  }
  
  if (name.includes('car') || name.includes('fortuner') || name.includes('harrier') || name.includes('kiger') || name.includes('safari')) {
    // Renault Kiger / Compact SUV class (< 12L)
    if (name.includes('kiger') || name.includes('magnite') || name.includes('kwid') || (amount > 0 && amount < 1200000)) {
      return require('../../images/car_kiger_3d.png');
    }
    // Mid SUV / Harrier class (>= 12L and < 28L)
    if (name.includes('harrier') || name.includes('safari') || name.includes('nexon') || name.includes('creta') || (amount >= 1200000 && amount < 2800000)) {
      return require('../../images/car_harrier_3d.png');
    }
    // Large SUV / Fortuner class (>= 28L and < 50L)
    if (name.includes('fortuner') || name.includes('endeavour') || (amount >= 2800000 && amount < 5000000)) {
      return require('../../images/car_fortuner_3d.png');
    }
    // Luxury SUV / Sedan class (>= 50L)
    return require('../../images/car_luxury_3d.png');
  }
  
  if (name.includes('house') || name.includes('flat') || name.includes('villa') || name.includes('home')) {
    return require('../../images/reward_house_3d.png');
  }
  if (name.includes('share') || name.includes('stock')) {
    return require('../../images/reward_shares_3d.png');
  }
  if (name.includes('cash') || name.includes('fund') || name.includes('bonus')) {
    const levelNum = Number(level) || 0;
    if (levelNum <= 3) {
      return require('../../images/cash_bonus_3d.png');
    } else if (levelNum === 4) {
      return require('../../images/cash_bonus_inr_3d.png');
    } else {
      return require('../../images/reward_shares_3d.png');
    }
  }
  
  // Default fallback
  return require('../../images/reward_car_3d.png');
};

const cleanDesignationName = (name, level) => {
  if (!name) return '';
  const levelPattern = new RegExp(`^level\\s*${level}\\s*-?\\s*`, 'i');
  return name.replace(levelPattern, '').trim();
};

const RewardList = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [combinedLevels, setCombinedLevels] = useState([]);
  const [flippedCards, setFlippedCards] = useState({});

  const handleCardFlip = (key) => {
    setFlippedCards(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [rewardRes, incentiveRes] = await Promise.all([
        levelService.getLevels({ benefitType: 'reward', limit: 1000, status: 'active' }),
        levelService.getLevels({ benefitType: 'incentive', limit: 1000, status: 'active' })
      ]);

      const rewards = (rewardRes.status && Array.isArray(rewardRes.data)) ? rewardRes.data : [];
      const incentives = (incentiveRes.status && Array.isArray(incentiveRes.data)) ? incentiveRes.data : [];

      // Merge rewards and incentives by designationLevel
      const levelsMap = new Map();

      rewards.forEach(r => {
        const key = r.designationLevel;
        if (key !== undefined && key !== null) {
          if (!levelsMap.has(key)) levelsMap.set(key, { reward: null, incentive: null, level: key });
          levelsMap.get(key).reward = r;
        }
      });

      incentives.forEach(i => {
        const key = i.designationLevel;
        if (key !== undefined && key !== null) {
          if (!levelsMap.has(key)) levelsMap.set(key, { reward: null, incentive: null, level: key });
          levelsMap.get(key).incentive = i;
        }
      });

      const combinedArray = Array.from(levelsMap.values())
        .filter(item => item.level !== 1 && item.level !== 2)
        .sort((a, b) => (a.level || 0) - (b.level || 0));
      window.__combinedLevels = combinedArray;
      setCombinedLevels(combinedArray);
    } catch (error) {
      console.error('Error fetching levels data:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasReward = (r) => {
    return r && (r.rewardAmount > 0 || (r.rewardItem && r.rewardItem.trim() !== ''));
  };

  const hasIncentive = (i) => {
    return i && i.incentiveAmount > 0;
  };

  const renderCard = (data, type) => {
    const isReward = type === 'reward';
    const titleColor = isReward ? theme.palette.primary.main : theme.palette.warning.main;
    
    // Values
    const benefitName = isReward 
      ? (data.rewardDescription || data.rewardItem || 'Special Reward')
      : `₹${(data.incentiveAmount || 0).toLocaleString()} Cash Bonus`;
      
    const apiImage = data.image || data.rewardImage || data.incentiveImage || data.rewardItemImage || data.benefitImage;
    let imageSrc;
    if (apiImage) {
      if (apiImage.startsWith('http://') || apiImage.startsWith('https://') || apiImage.startsWith('data:')) {
        imageSrc = apiImage;
      } else {
        const baseURL = process.env.REACT_APP_API_BASE_URL || '';
        const base = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        const relativePath = apiImage.startsWith('/') ? apiImage : `/${apiImage}`;
        imageSrc = `${base}${relativePath}`;
      }
    } else {
      imageSrc = isReward 
        ? getRewardImageSrc(benefitName, data.rewardAmount, data.designationLevel)
        : getRewardImageSrc(benefitName, data.incentiveAmount, data.designationLevel);
    }

    const targetSelf = data.toSelfBusiness || data.minSelfBusiness || 0;
    const targetTeam = data.toTeamBusiness || data.minTeamBusiness || 0;

    const cardKey = `${data.designationLevel}-${type}`;
    const isFlipped = flippedCards[cardKey];

    return (
      <Card
        sx={{
          width: '100%',
          height: '300px',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
          border: '1px solid rgba(255,255,255,0.4)',
          position: 'relative',
          display: 'flex',
          flexDirection: isReward ? 'row' : 'row-reverse',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* IMAGE CONTAINER */}
        <Box
          onClick={() => handleCardFlip(cardKey)}
          sx={{
            position: 'relative',
            width: isFlipped ? '0%' : '100%',
            height: '100%',
            overflow: 'hidden',
            cursor: 'pointer',
            opacity: isFlipped ? 0 : 1,
            transform: isFlipped ? (isReward ? 'translateX(-100%) scale(0.9)' : 'translateX(100%) scale(0.9)') : 'none',
            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease, transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            flexShrink: 0,
            '&:hover .overlay-action': {
              opacity: isFlipped ? 0 : 1,
              transform: 'translate(-50%, -50%) scale(1)'
            },
            '&:hover img': {
              transform: isFlipped ? 'none' : 'scale(1.05)'
            }
          }}
        >
          <CardMedia
            component="img"
            image={imageSrc}
            alt={benefitName}
            sx={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              transition: 'transform 0.5s ease'
            }}
          />
          {/* Top Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              left: 20,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '14px',
              px: 2,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              opacity: isFlipped ? 0 : 1,
              transform: isFlipped ? 'scale(0.8)' : 'scale(1)',
              transition: 'all 0.4s ease',
              pointerEvents: isFlipped ? 'none' : 'auto',
            }}
          >
            <TrophyIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
            <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.85rem', textTransform: 'capitalize' }}>
              {type} Level {data.designationLevel}
            </Typography>
          </Box>

          {/* Bottom Info Gradient */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
              p: 2,
              pt: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              opacity: isFlipped ? 0 : 1,
              transform: isFlipped ? 'translateY(20px)' : 'translateY(0)',
              transition: 'all 0.4s ease',
              pointerEvents: isFlipped ? 'none' : 'auto',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, color: 'white', mb: 0.25, fontSize: '1.15rem' }}>
                {data.designationName || 'Associate Rank'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                Click to view targets & details
              </Typography>
            </Box>
            <Box
              sx={{
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                borderRadius: '12px',
                px: 1.5,
                py: 0.5,
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                {data.percentage || 0}% Comm
              </Typography>
            </Box>
          </Box>

          {/* Hover Action Overlay */}
          <Box
            className="overlay-action"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) scale(0.9)',
              opacity: 0,
              transition: 'all 0.3s ease',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(5px)',
              borderRadius: '50%',
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
            }}
          >
            <TrophyIcon sx={{ color: titleColor, fontSize: 32 }} />
          </Box>
        </Box>

        {/* DETAILS CONTAINER */}
        <Box
          sx={{
            width: isFlipped ? '100%' : '0%',
            height: '100%',
            opacity: isFlipped ? 1 : 0,
            transform: isFlipped ? 'translateX(0)' : (isReward ? 'translateX(50px)' : 'translateX(-50px)'),
            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease 0.1s, transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ width: '100%', minWidth: { xs: '220px', sm: '280px', md: '320px' }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1.1 }}>
                    {data.designationName || 'Associate Rank'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, fontSize: '0.65rem' }}>
                    {type.toUpperCase()} LEVEL {data.designationLevel} DETAILS
                  </Typography>
                </Box>
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardFlip(cardKey);
                  }}
                  size="small"
                  sx={{ p: 0.5, bgcolor: 'rgba(0,0,0,0.04)', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              <Divider sx={{ mb: 1, opacity: 0.4 }} />

              <Box sx={{ 
                mb: 1.25, 
                p: 1.25, 
                borderRadius: '12px', 
                bgcolor: `${titleColor}08`, 
                border: `1px solid ${titleColor}15`,
                display: 'flex',
                gap: 1.5,
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '8px', 
                  bgcolor: `${titleColor}15`, 
                  color: titleColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TrophyIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.1, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Benefit Received
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900, color: titleColor, lineHeight: 1.2 }}>
                    {benefitName}
                  </Typography>
                  {isReward && data.rewardDescription && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25, fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.1 }}>
                      {data.rewardDescription}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ mb: 1.25 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.75, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Targets Required
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={targetSelf > 0 ? 6 : 12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '10px', border: '1px solid rgba(0,0,0,0.05)', bgcolor: 'rgba(0,0,0,0.01)' }}>
                      <Box sx={{ p: 0.75, borderRadius: '6px', bgcolor: `${theme.palette.info.main}12`, color: theme.palette.info.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GroupsIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, fontSize: '0.65rem', mb: 0 }}>Team Business</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1 }}>
                          ₹{targetTeam.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  {targetSelf > 0 && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '10px', border: '1px solid rgba(0,0,0,0.05)', bgcolor: 'rgba(0,0,0,0.01)' }}>
                        <Box sx={{ p: 0.75, borderRadius: '6px', bgcolor: `${theme.palette.success.main}12`, color: theme.palette.success.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PersonIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, fontSize: '0.65rem', mb: 0 }}>Personal Business</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1 }}>
                            ₹{targetSelf.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box sx={{ mt: 'auto', p: 1, px: 1.5, borderRadius: '10px', border: '1px solid rgba(0,0,0,0.04)', bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Commission Rate
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900, color: titleColor }}>
                  {data.percentage || 0}%
                </Typography>
              </Box>
            </CardContent>
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <Box sx={{
      p: { xs: 2, md: 4 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrophyIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
              Rewards & Incentives
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Explore the exciting rewards and cash bonuses configured for each level.
            </Typography>
          </Box>
        </Box>
        <RefreshButton onClick={fetchData} loading={loading} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box>
          {combinedLevels.map((item, index) => {
            const showReward = hasReward(item.reward);
            const showIncentive = hasIncentive(item.incentive);
            
            if (!showReward && !showIncentive) return null;

            return (
              <Box key={index} sx={{ mb: 6 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 3, 
                  pb: 1.5,
                  borderBottom: '2px solid',
                  borderColor: 'rgba(0, 0, 0, 0.05)',
                  position: 'relative'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '14px',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 900,
                      fontSize: '1.4rem',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                    }}>
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {cleanDesignationName(item.reward?.designationName || item.incentive?.designationName || 'Associate', item.level)}
                        <Chip 
                          label={`Level ${item.level}`} 
                          size="small" 
                          sx={{ 
                            fontWeight: 800, 
                            bgcolor: `${theme.palette.primary.main}12`, 
                            color: 'primary.main',
                            fontSize: '0.75rem',
                            borderRadius: '8px'
                          }} 
                        />
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
                        Configure qualifications and earn rewards for achieving this milestone.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Grid container spacing={4}>
                  {showReward && (
                    <Grid item xs={12} md={showIncentive ? 6 : 12}>
                      {renderCard(item.reward, 'reward')}
                    </Grid>
                  )}
                  {showIncentive && (
                    <Grid item xs={12} md={showReward ? 6 : 12}>
                      {renderCard(item.incentive, 'incentive')}
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })}
        </Box>
      )}    </Box>
  );
};

export default RewardList;
